import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Minus, Star, Users, Zap, Crown } from 'lucide-react';
import { mockData } from '../../mock/data';

const comparisonFeatures = [
  {
    category: 'Ad Operations',
    features: [
      { name: 'Campaign setup & trafficking', starter: true, growth: true, enterprise: true },
      { name: 'Performance monitoring', starter: true, growth: true, enterprise: true },
      { name: 'Platform troubleshooting', starter: true, growth: true, enterprise: true },
      { name: 'Bid optimisation', starter: false, growth: true, enterprise: true },
      { name: 'Budget pacing & management', starter: false, growth: true, enterprise: true },
      { name: 'Quality assurance & pre-flight', starter: false, growth: true, enterprise: true }
    ]
  },
  {
    category: 'Analytics & Reporting',
    features: [
      { name: 'White-label performance reports', starter: true, growth: true, enterprise: true },
      { name: 'Custom dashboards', starter: true, growth: true, enterprise: true },
      { name: 'Attribution analysis', starter: true, growth: true, enterprise: true },
      { name: 'ROI tracking', starter: false, growth: true, enterprise: true },
      { name: 'Competitive analysis', starter: false, growth: true, enterprise: true },
      { name: 'Predictive insights', starter: false, growth: true, enterprise: true }
    ]
  },
  {
    category: 'Creative Operations',
    features: [
      { name: 'Creative production', starter: false, growth: false, enterprise: true },
      { name: 'A/B testing setup & management', starter: false, growth: false, enterprise: true },
      { name: 'Asset organisation & management', starter: false, growth: false, enterprise: true },
      { name: 'Brand compliance QA', starter: false, growth: false, enterprise: true },
      { name: 'Performance-based creative strategy', starter: false, growth: false, enterprise: true }
    ]
  },
  {
    category: 'Support & Communication',
    features: [
      { name: 'Email support', starter: true, growth: true, enterprise: true },
      { name: 'Slack integration', starter: true, growth: true, enterprise: true },
      { name: 'Weekly reports', starter: true, growth: true, enterprise: true },
      { name: 'Monthly strategy reviews', starter: false, growth: true, enterprise: true },
      { name: 'Quarterly planning sessions', starter: false, growth: true, enterprise: true },
      { name: 'Dedicated account manager', starter: false, growth: true, enterprise: true }
    ]
  }
];

const includedInAllPlans = [
  'White-label delivery on all work',
  'Platform-certified specialists only',
  'US ad market expertise',
  'Best-practice campaign structures',
  'Regular performance & strategy updates',
  'Transparent, auditable reporting'
];

const faqItems = [
  {
    question: 'How quickly can we get our agency onboarded?',
    answer: 'Most agencies are fully operational within 48 hours of signing. We conduct a quick brief on your client accounts, assign your dedicated specialists, grant access, and begin execution immediately. No lengthy onboarding or training required on your end.'
  },
  {
    question: 'Will your team work under our agency brand?',
    answer: "Yes — always. Every deliverable, report, dashboard, and client communication is produced under your brand. Your clients never see the Marqi name. We're your team, operating as your team."
  },
  {
    question: 'Can we change plans or cancel if our client roster changes?',
    answer: "Yes. You can upgrade, downgrade, or cancel with 30 days' notice. We're designed to flex with your agency's growth — scale your pod up when you win a big client, adjust if things slow down. No lock-in penalties."
  },
  {
    question: 'Which platforms do your specialists cover?',
    answer: 'We have certified specialists across Google Ads (Search, Shopping, YouTube), Meta (Facebook, Instagram), Microsoft Advertising, Amazon Ads (Sponsored & DSP), TikTok, and LinkedIn. For programmatic (DV360, The Trade Desk), ask about our Enterprise Pod.'
  },
  {
    question: 'Do you provide training or handoff documentation for our internal team?',
    answer: 'Yes — particularly for Growth and Enterprise clients. We produce documented workflows, naming conventions, and optimisation playbooks so your internal team always has visibility into how accounts are structured and managed.'
  },
  {
    question: 'How do you handle data security and client account access?',
    answer: "We follow SOC 2-aligned security protocols with encrypted data transmission, strict role-based access controls, and no storage of client credentials outside approved tools. We work within your clients' existing ad accounts — we never take ownership."
  },
  {
    question: "What happens if our client's ad spend changes significantly mid-contract?",
    answer: "We'll adjust your pod accordingly. If a client scales up significantly, we'll recommend upgrading your pod tier to ensure coverage quality doesn't slip. If spend drops, we'll work with you to right-size. Your account manager handles this proactively."
  }
];

const gettingStartedSteps = [
  {
    step: '01',
    title: 'Schedule a Consultation',
    description: "Book a 30-minute call. We'll review your current client portfolio, platform mix, and ops gaps — no hard sell, just a clear picture of fit."
  },
  {
    step: '02',
    title: 'Get Your Custom Pod Proposal',
    description: "Within 24 hours you'll receive a tailored proposal — recommended pod size, team composition, and a clear scope of what we'll handle from day one."
  },
  {
    step: '03',
    title: 'Launch in 48 Hours',
    description: 'Your dedicated ops team gets access, completes the account brief, and begins execution. Most agencies see their first optimisations within the first week.'
  }
];

const Cell = ({ on }) => (
  on
    ? <Check className="w-5 h-5 text-[var(--accent-primary)] mx-auto" />
    : <Minus className="w-4 h-4 text-[var(--text-muted)] mx-auto" />
);

const PricingPage = () => {
  const { pricingTiers } = mockData;

  return (
    <div className="min-h-screen py-20" data-testid="pricing-page">
      <div className="container">
        {/* Hero Section */}
        <div className="max-w-4xl mx-auto text-center mb-20">
          <h1 className="display-md text-[var(--text-primary)] mb-6" data-testid="pricing-hero-title">
            Predictable Pricing. Dedicated Team. Zero Hiring Headaches.
          </h1>
          <p className="body-lg mb-12">
            Every MarqiOps plan gives your agency a certified operations team that works inside your
            client accounts, delivers under your brand, and scales as your roster grows. No long-term
            contracts. No surprise costs.
          </p>

          <div className="flex flex-col sm:flex-row gap-6 justify-center">
            <Link to="/contact" className="btn-primary" data-testid="pricing-hero-cta-primary">
              Schedule a Consultation
              <ArrowRight className="ml-2 w-5 h-5" />
            </Link>
            <Link to="/ops" className="btn-secondary" data-testid="pricing-hero-cta-secondary">
              Learn About Services
            </Link>
          </div>
        </div>

        {/* Start Small — Entry Offers */}
        <section className="mb-24">
          <div className="text-center mb-12">
            <h2 className="h1 text-[var(--text-primary)] mb-4">Not Ready to Commit? Start Small.</h2>
            <p className="body-lg max-w-3xl mx-auto">
              Two low-risk ways to see MarqiOps in action before you scale into a full pod.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            <div className="feature-card border-t-2 border-t-[var(--accent-primary)]" data-testid="pricing-trial-week">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="h2 text-[var(--text-primary)]">Trial Week</h3>
                <div><span className="h1 text-[var(--accent-primary)]">$500</span><span className="text-[var(--text-muted)]"> flat</span></div>
              </div>
              <p className="body-md mb-6">
                One week of full MarqiOps delivery. If it works, move to a monthly plan. If not, you've lost
                a week — not a year. No contract.
              </p>
              <ul className="space-y-3 mb-8">
                {['One campaign setup', 'One week of campaign monitoring', 'One white-label performance report', 'No contract, no lock-in'].map((f) => (
                  <li key={f} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)] text-left">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn-primary w-full" data-testid="pricing-trial-week-cta">
                Start a Trial Week<ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
            <div className="feature-card" data-testid="pricing-essentials-pod">
              <div className="flex items-baseline justify-between mb-4">
                <h3 className="h2 text-[var(--text-primary)]">Essentials Pod</h3>
                <div><span className="h1 text-[var(--accent-primary)]">$1,500</span><span className="text-[var(--text-muted)]">/month</span></div>
              </div>
              <p className="body-md mb-6">
                For agencies managing up to $30K monthly client ad spend. A dedicated entry pod to get started.
              </p>
              <ul className="space-y-3 mb-8">
                {['1 dedicated ops specialist', 'Campaign setup & trafficking (up to 3/month)', 'Weekly white-label performance reports', 'Daily monitoring & anomaly alerts', 'Email support', 'White-label delivery included'].map((f) => (
                  <li key={f} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                    <span className="text-[var(--text-secondary)] text-left">{f}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn-secondary w-full" data-testid="pricing-essentials-pod-cta">
                Get Started<ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Pricing Tiers */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Choose Your Agency's Ops Pod
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              All pods include white-label delivery, platform-certified specialists, and the same
              battle-tested workflows — the difference is team size, spend coverage, and depth of service.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {pricingTiers.map((tier, index) => (
              <div key={tier.id} className={`feature-card text-center relative ${tier.popular ? 'border-[var(--accent-primary)] transform scale-105' : ''} animate-fade-in`} style={{ animationDelay: `${index * 0.2}s` }}>
                {tier.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[var(--accent-primary)] text-[var(--bg-primary)] px-6 py-2 rounded-full text-sm font-semibold flex items-center">
                      <Star className="w-4 h-4 mr-2" />
                      Most Popular
                    </div>
                  </div>
                )}

                <div className="mb-8">
                  <div className="w-16 h-16 bg-[var(--accent-bg)] rounded-full flex items-center justify-center mx-auto mb-6">
                    {tier.name.includes('Starter') && <Users className="w-8 h-8 text-[var(--accent-primary)]" />}
                    {tier.name.includes('Growth') && <Zap className="w-8 h-8 text-[var(--accent-primary)]" />}
                    {tier.name.includes('Enterprise') && <Crown className="w-8 h-8 text-[var(--accent-primary)]" />}
                  </div>

                  <h3 className="h2 text-[var(--text-primary)] mb-4">{tier.name}</h3>

                  <div className="mb-6">
                    <span className="h1 text-[var(--accent-primary)]">{tier.price}</span>
                    <span className="text-[var(--text-muted)]">/{tier.period}</span>
                  </div>

                  <p className="body-md">{tier.description}</p>
                </div>

                <ul className="space-y-4 mb-8">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start space-x-3">
                      <Check className="w-5 h-5 text-[var(--accent-primary)] flex-shrink-0 mt-0.5" />
                      <span className="text-[var(--text-secondary)] text-left">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className={`w-full ${tier.popular ? 'btn-primary' : 'btn-secondary'}`}
                  data-testid={`pricing-tier-cta-${tier.id}`}
                >
                  {tier.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Link>
              </div>
            ))}
          </div>

          <p className="text-center text-[var(--text-muted)] mt-10">
            <Star className="w-4 h-4 text-[var(--accent-primary)] inline mr-2 -mt-1" />
            Growth Pod is most popular for agencies with 5–20 active clients.
          </p>
        </section>

        {/* Feature Comparison */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Plan Comparison — Full Breakdown
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Every line item your ops team handles, mapped to each pod so you know exactly what
              you're getting before you sign.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-[var(--border-primary)]">
                  <th className="text-left p-4 text-[var(--text-primary)] font-semibold">Features</th>
                  <th className="text-center p-4 text-[var(--text-primary)] font-semibold">Starter Pod</th>
                  <th className="text-center p-4 text-[var(--text-primary)] font-semibold">Growth Pod</th>
                  <th className="text-center p-4 text-[var(--text-primary)] font-semibold">Enterprise Pod</th>
                </tr>
              </thead>
              <tbody>
                {comparisonFeatures.map((category) => (
                  <React.Fragment key={category.category}>
                    <tr className="bg-[var(--bg-secondary)]">
                      <td colSpan="4" className="p-4 font-semibold text-[var(--accent-primary)]">
                        {category.category}
                      </td>
                    </tr>
                    {category.features.map((feature) => (
                      <tr key={feature.name} className="border-b border-[var(--border-subtle)]">
                        <td className="p-4 text-[var(--text-secondary)]">{feature.name}</td>
                        <td className="text-center p-4"><Cell on={feature.starter} /></td>
                        <td className="text-center p-4"><Cell on={feature.growth} /></td>
                        <td className="text-center p-4"><Cell on={feature.enterprise} /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Included in All Plans */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Non-Negotiables. Included Everywhere.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Every MarqiOps pod — regardless of tier — comes with these foundational guarantees.
              Because your agency's reputation depends on consistency, not just the size of your contract.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {includedInAllPlans.map((item, index) => (
              <div key={item} className={`flex items-center p-6 bg-[var(--bg-secondary)] rounded-lg animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                <Check className="w-6 h-6 text-[var(--accent-primary)] mr-4 flex-shrink-0" />
                <span className="text-[var(--text-secondary)]">{item}</span>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-24">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Questions Agencies Actually Ask.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Straight answers — no sales fluff.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {faqItems.map((faq, index) => (
              <div key={faq.question} className={`feature-card animate-fade-in`} style={{ animationDelay: `${index * 0.1}s` }}>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{faq.question}</h3>
                <p className="body-md">{faq.answer}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Getting Started */}
        <section className="mb-24 bg-[var(--bg-secondary)] -mx-6 px-6 py-16 rounded-2xl">
          <div className="text-center mb-16">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Three Steps to Your Dedicated Ops Team.
            </h2>
            <p className="body-lg max-w-3xl mx-auto">
              Most agencies go from first conversation to live campaigns in under 48 hours.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            {gettingStartedSteps.map((step, index) => (
              <div key={step.step} className={`animate-fade-in`} style={{ animationDelay: `${index * 0.2}s` }}>
                <div className="w-16 h-16 bg-[var(--accent-primary)] rounded-full flex items-center justify-center mx-auto mb-6">
                  <span className="text-2xl font-bold text-[var(--bg-primary)]">{step.step}</span>
                </div>
                <h3 className="h3 text-[var(--text-primary)] mb-4">{step.title}</h3>
                <p className="body-md">{step.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="feature-card">
            <h2 className="h1 text-[var(--text-primary)] mb-6">
              Your Agency Doesn't Need More Staff. It Needs the Right Partner.
            </h2>
            <p className="body-lg mb-12 max-w-3xl mx-auto">
              Growing agencies trust MarqiOps to run their client campaigns — cleanly,
              quickly, and always under their brand. If you're managing more than two active clients
              and your team is stretched, it's time to talk.
            </p>

            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link to="/contact" className="btn-primary glow-effect" data-testid="pricing-final-cta-primary">
                Start Your Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
              <Link to="/ops/ad-operations" className="btn-secondary" data-testid="pricing-final-cta-secondary">
                Compare Full Plan Details
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default PricingPage;
